<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 550px)' : 560"
    persistent
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Final Invoice Settings</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          @click="showPreview = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="final-invoice-dialog-layout">
          <div class="final-invoice-form-pane">
            <div class="pa-6">
              <v-alert
                v-if="finalInvoiceCompatibilityError"
                dense
                outlined
                type="error"
                class="mb-4"
              >
                {{ finalInvoiceCompatibilityError }}
              </v-alert>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenus.fulfillmentDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="localForm.fulfillmentDate"
                        label="Date of completion"
                        outlined
                        dense
                        hint="Optional"
                        persistent-hint
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="localForm.fulfillmentDate = null"
                      />
                    </template>

                    <v-date-picker
                      v-model="localForm.fulfillmentDate"
                      no-title
                      @input="dateMenus.fulfillmentDate = false"
                    />
                  </v-menu>

                  <div class="d-flex flex-column mt-2 mb-2">
                    <div>Add days:</div>
                    <div class="d-flex flex-wrap">
                      <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 0)">Today</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 1)">+1</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 2)">+2</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 3)">+3</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 5)">+5</v-btn>
                      <v-btn text @click="setTodayPlusDays('fulfillmentDate', 8)">+8</v-btn>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenus.paymentDeadline"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="localForm.paymentDeadline"
                        label="Payment due date"
                        outlined
                        dense
                        hint="Optional"
                        persistent-hint
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="localForm.paymentDeadline = null"
                      />
                    </template>

                    <v-date-picker
                      v-model="localForm.paymentDeadline"
                      no-title
                      @input="dateMenus.paymentDeadline = false"
                    />
                  </v-menu>

                  <div class="d-flex flex-column mt-2 mb-2">
                    <div>Add days:</div>
                    <div class="d-flex flex-wrap">
                      <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 0)">Today</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 1)">+1</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 2)">+2</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 3)">+3</v-btn>
                      <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 5)">+5</v-btn>
                      <v-btn text @click="setTodayPlusDays('paymentDeadline', 8)">+8</v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-textarea
                v-model="localForm.note"
                label="Comment"
                outlined
                dense
                auto-grow
                rows="3"
                hint="Optional"
                persistent-hint
              />
            </div>
          </div>

          <v-expand-x-transition>
            <div v-if="showPreview" class="final-invoice-preview-pane">
              <v-divider vertical />

              <div class="pa-6 preview-pane-inner">
                <InvoicePreviewPanel
                  document-type="final_invoice"
                  :order="invoiceDatas"
                  :settings="previewSettings"
                  :seller="seller"
                  :currency="computeSuffix"
                />
              </div>
            </div>
          </v-expand-x-transition>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          v-if="invoiceDatas"
          color="primary"
          class="mr-2"
          :disabled="loading || Boolean(finalInvoiceCompatibilityError)"
          @click="openPreview"
        >
          {{ !showPreview ? 'Preview final invoice' : 'Close preview' }}
        </v-btn>

        <v-btn text @click="closeDialog"
          :disabled="loading"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          @click="submit"
          :loading="loading"
          :disabled="Boolean(finalInvoiceCompatibilityError)"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InvoicePreviewPanel from './InvoicePreviewPanel.vue';
import Swal from "sweetalert2";


export default {
  name: 'FinalInvoiceDialog',

  components: {
    InvoicePreviewPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({
        id: null,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
        proformaNumber: null,
      }),
    },
    invoiceDatas: {
      type: Object,
      default: () => null,
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
  },

  data() {
    return {
      showPreview: false,
      dateMenus: {
        fulfillmentDate: false,
        paymentDeadline: false,
        dueDate: false,
      },
      datas: null,
      localForm: {
        id: null,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
        proformaNumber: null,
      },
      proformas: [],
    }
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    computeSuffix() {
      return this.datas?.priceList?.currency_id ?? 'HUF'
    },

    finalInvoiceCompatibilityError() {
      const orderVatCodes = this.collectOrderVatCodes()

      if (orderVatCodes.length <= 1) {
        return null
      }

      const deposits = this.getUsableDepositInvoices()

      for (const deposit of deposits) {
        const payload = this.normalizeDocumentPayload(deposit)
        const breakdown = Array.isArray(payload.vat_breakdown) ? payload.vat_breakdown : []
        const depositVatCodes = [...new Set(
          breakdown
            .map(line => this.normalizeVatCode(line?.tax_rate || line?.vat))
            .filter(Boolean)
        )].sort()

        if (JSON.stringify(depositVatCodes) !== JSON.stringify([...orderVatCodes].sort())) {
          return 'The VAT breakdown of an applicable deposit invoice does not match the current Sales Order VAT breakdown. Reverse the old deposit invoice and create a new VAT-grouped summary or itemized advance chain before generating the final invoice.'
        }
      }

      return null
    },

    previewSettings() {
      return {
        id: this.orderId,
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
        proformaNumber: this.localForm.proformaNumber || this.initialData?.proformaNumber || null,
      }
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.syncForm()
        } else {
          this.showPreview = false
        }
      },
    },

    initialData: {
      deep: true,
      handler() {
        if (this.value) {
          this.syncForm()
        }
      },
    },

    invoiceDatas: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.datas = val
          this.calculateWeight()
          if(val?.documents){
            this.proformas = val?.documents.filter(doc => doc.type === 'proforma' && doc.status === 'paid');
          }
        }
      },
    },
  },

  methods: {
    normalizeBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeDocumentPayload(document) {
      const payload = document?.extra_payload

      if (!payload) return {}
      if (typeof payload === 'object') return payload

      try {
        const parsed = JSON.parse(payload)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    },

    normalizeVatCode(value) {
      if (value === null || value === undefined || value === '') return null
      return String(value).trim().toUpperCase().replace(',', '.')
    },

    resolveOrderItemVat(item) {
      const currency = String(this.invoiceDatas?.priceList?.currency_id || '').toUpperCase()
      const zeroTax = this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      const reverseTax = this.normalizeBoolean(this.invoiceDatas?.reverse_tax)
      const isService = this.normalizeBoolean(item?.service) || this.normalizeBoolean(item?.item?.service)

      if (zeroTax) {
        const stored = this.normalizeVatCode(item?.vat_name)
        return stored && Number.isNaN(Number(stored)) ? stored : (currency === 'EUR' ? 'EUT' : 'F.AFA')
      }

      if (reverseTax && !isService) return 'F.AFA'

      return this.normalizeVatCode(item?.vat_name)
        || this.normalizeVatCode(item?.vat_value)
        || this.normalizeVatCode(this.invoiceDatas?.priceList?.vat_value)
        || (currency === 'EUR' ? 'EUT' : '27')
    },

    collectOrderVatCodes() {
      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      return [...new Set(items.map(item => this.resolveOrderItemVat(item)).filter(Boolean))]
    },

    isDocumentReversed(document) {
      const status = String(document?.status || '').toLowerCase()
      const reverseStatus = String(document?.reverse_status || '').toLowerCase()

      return String(document?.type || '').toLowerCase() === 'reverse_invoice'
        || status === 'reversed'
        || reverseStatus === 'reversed'
        || Boolean(document?.reversed_by_document_id)
    },

    getUsableDepositInvoices() {
      const documents = Array.isArray(this.invoiceDatas?.documents) ? this.invoiceDatas.documents : []

      return documents.filter(document => {
        return String(document?.type || '').toLowerCase() === 'deposit_invoice'
          && String(document?.status || '').toLowerCase() === 'paid'
          && !this.isDocumentReversed(document)
      })
    },

    resolveAdvanceDocumentMode(document, payload = this.normalizeDocumentPayload(document)) {
      const storedMode = String(payload.advance_mode || '').toLowerCase()

      if (storedMode === 'itemized' || storedMode === 'single') return storedMode
      if (payload.include_order_items !== undefined) {
        return this.normalizeBoolean(payload.include_order_items) ? 'itemized' : 'single'
      }

      const breakdown = Array.isArray(payload.vat_breakdown) ? payload.vat_breakdown : []
      const vatCodes = [...new Set(breakdown.map(line => this.normalizeVatCode(line?.tax_rate || line?.vat)).filter(Boolean))]

      return String(document?.tax_rate || '').toUpperCase() === 'MIXED' || vatCodes.length > 1
        ? 'itemized'
        : 'single'
    },

    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    setTodayPlusDays(field, days) {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + days)
      this.localForm[field] = this.formatDateForInput(date)
    },

    syncForm() {
      this.localForm = {
        id: this.orderId,
        fulfillmentDate: this.initialData?.fulfillmentDate || null,
        paymentDeadline: this.initialData?.paymentDeadline || null,
        note: this.initialData?.note || null,
        proformaNumber: this.initialData?.proformaNumber || null,
      }

      this.calculateWeight()
    },

    openPreview() {
      if (this.finalInvoiceCompatibilityError) {
        Swal.fire('Cannot generate final invoice', this.finalInvoiceCompatibilityError, 'error')
        return
      }

      if (this.showPreview) {
        this.showPreview = false
        return
      }

      this.showPreview = true
    },

    closeDialog() {
      this.showPreview = false
      this.syncForm()
      this.dialogProxy = false
    },

    submit() {
      if (this.finalInvoiceCompatibilityError) {
        Swal.fire('Cannot generate final invoice', this.finalInvoiceCompatibilityError, 'error')
        return
      }

      const payload = {
        id: this.orderId,
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
        proformaNumber: this.localForm.proformaNumber || null,
      }

      Swal.fire({
          title: `<strong>ALERT</strong>`,
          icon: "warning",
          width: "600px",
          text: "Are you sure you want to create the final invoice? This action cannot be undone.",
          showCloseButton: true,
          focusConfirm: false,
          confirmButtonText: "Yes, create it!",
          confirmButtonColor: "#00c300",
          infoColor: "#e33354",
          showCancelButton: true,
      }).then((result) => {
          if (result.isConfirmed) {
              this.$emit('save', payload)
              //this.dialogProxy = false
          }
      });
    },

    calculateWeight() {
      if (!this.invoiceDatas || !Array.isArray(this.invoiceDatas.items) || this.invoiceDatas.items.length <= 0) {
        return
      }

      if (this.initialData?.note) {
        this.localForm.note = this.initialData.note
        return
      }

      let sumWeight = 0

      this.invoiceDatas.items.forEach(element => {
        const unitWeight = Number(element.alt_unit_weight || 0) || Number(element.unit_weight || 0)
      
        if (unitWeight > 0) {
          sumWeight += unitWeight * Number(element.quantity || 0) * Number(element.num_of_pcs || 1)
        }
      })

      this.localForm.note = `Össztömeg: ${sumWeight.toFixed(1)} kg\n`
    },
  },
}
</script>

<style scoped>
.final-invoice-dialog-layout {
  display: flex;
  align-items: stretch;
  min-height: 620px;
}

.final-invoice-form-pane {
  flex: 0 0 560px;
  max-width: 560px;
}

.final-invoice-preview-pane {
  display: flex;
  flex: 1;
  min-width: 0;
  background: #fafafa;
}

.preview-pane-inner {
  flex: 1;
  min-width: 0;
  overflow: auto;
}
</style>